/**
 * True server-side impersonation (client side).
 *
 * startImpersonation → calls adminImpersonate (mints a custom token) → signs
 * in AS the target with that token. From then on the app runs with the
 * target's real identity (their data access + writes). A flag in sessionStorage
 * remembers who the real admin was so the banner + "Stop" can re-auth back.
 *
 * stopImpersonation → re-authenticates as the original admin via Google
 * (prefilled with their email). The flag is cleared only AFTER a successful
 * re-auth, so a cancelled popup leaves you still impersonating (retryable).
 *
 * Per-tab (sessionStorage), so closing the tab ends the impersonation context.
 */
import {
  signInWithCustomToken,
  signInWithPopup,
  GoogleAuthProvider,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../lib/firebase';
import { CALLABLES, type AdminImpersonateResponse } from '@daaily/shared';

const KEY = 'daaily.impersonation';

export interface ImpersonationFlag {
  adminEmail: string;
  adminName: string;
  targetEmail: string;
  targetName: string;
}

export function getImpersonation(): ImpersonationFlag | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as ImpersonationFlag) : null;
  } catch {
    return null;
  }
}

export async function startImpersonation(args: {
  targetEmail: string;
  adminEmail: string;
  adminName: string;
}): Promise<void> {
  const fn = httpsCallable<{ targetEmail: string }, AdminImpersonateResponse>(
    functions,
    CALLABLES.ADMIN_IMPERSONATE,
  );
  const res = await fn({ targetEmail: args.targetEmail.toLowerCase() });
  const flag: ImpersonationFlag = {
    adminEmail: args.adminEmail.toLowerCase(),
    adminName: args.adminName,
    targetEmail: res.data.targetEmail,
    targetName: res.data.targetName,
  };
  // Set the flag BEFORE swapping identity so the banner is present on the
  // first render under the target session.
  sessionStorage.setItem(KEY, JSON.stringify(flag));
  await signInWithCustomToken(auth, res.data.token);
}

export async function stopImpersonation(): Promise<void> {
  const flag = getImpersonation();
  const provider = new GoogleAuthProvider();
  if (flag?.adminEmail) {
    provider.setCustomParameters({ login_hint: flag.adminEmail, prompt: 'select_account' });
  }
  await signInWithPopup(auth, provider);
  sessionStorage.removeItem(KEY);
}

/**
 * Reconcile the per-tab flag with the AUTH TOKEN's `impersonatedBy` claim.
 * The Firebase session persists across tab closes but sessionStorage doesn't —
 * a reopened tab restored a custom-token session signed in AS the target with
 * NO banner and no way back (this hid the seed-user menu for its owner: they
 * were still signed in as the seed without realizing). Called on auth-state
 * ready; re-seeds the flag from the claim so the banner + Stop always render.
 */
export async function syncImpersonationFromToken(): Promise<void> {
  const user = auth.currentUser;
  if (!user) return;
  try {
    const res = await user.getIdTokenResult();
    const impersonatedBy = res.claims['impersonatedBy'];
    const flag = getImpersonation();
    const email = (user.email ?? '').toLowerCase();
    if (typeof impersonatedBy === 'string' && impersonatedBy && !flag) {
      const seeded: ImpersonationFlag = {
        adminEmail: impersonatedBy.toLowerCase(),
        adminName: impersonatedBy,
        targetEmail: email,
        targetName: user.displayName || user.email || 'impersonated user',
      };
      sessionStorage.setItem(KEY, JSON.stringify(seeded));
      // Nudge listeners (Layout reads the flag on render) — a storage event
      // doesn't fire in the same tab, so force a re-render via a custom event.
      window.dispatchEvent(new Event('daaily-impersonation-sync'));
    } else if (flag && !impersonatedBy && flag.targetEmail.toLowerCase() !== email) {
      // STALE flag: the user signed out of an impersonation and back in as
      // themselves IN THE SAME TAB — sessionStorage survives sign-out, the
      // banner hides (email mismatch) but the flag kept suppressing the
      // seed-user menu with no visible reason. Clear it.
      sessionStorage.removeItem(KEY);
      window.dispatchEvent(new Event('daaily-impersonation-sync'));
    }
  } catch {
    /* best-effort — worst case the banner appears on next full load */
  }
}
